import React, { useEffect, useRef } from "react";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { BackgroundColorElement, BackgroundColorElementActive, BorderPrimary, FontColorHighlight, FontColorLowlight, FontColorWarn, GUIHeadline, GUIHeadlineSuffix, GUISelectableText, GUISubBox, GUITextDebug } from "../GameUIElements";
import styled from "styled-components";
import { toHumanReadableId } from "../../../../../shared/helper/actionTreeHelper";
import { IoIosCopy } from "react-icons/io";
import { VscClearAll } from "react-icons/vsc";
import { UiConstants } from "../../../../data/UiConstants";
import { SectionHeader } from "./GameDebugInfoUI";
import { InputWithMargin } from "../../../editor/Input";
import { gameStore } from "../../../../stores/GameStore";
import { reaction } from "mobx";
import { useThrottledForceUpdate } from "../../../../helper/reactHelpers";
const LogContainer = styled(GUISubBox).withConfig({
  displayName: "DebugGameHistoryLogUI__LogContainer",
  componentId: "sc-1t8gymb-0"
})(["display:flex;flex-direction:column;min-width:500px;height:100%;overflow-y:scroll;"]);
const LogEntryContainer = styled.div.withConfig({
  displayName: "DebugGameHistoryLogUI__LogEntryContainer",
  componentId: "sc-1t8gymb-1"
})(["display:flex;flex-direction:row;border-top:", ";padding-top:8px;"], props => props.useSeparator ? "1px solid " + BackgroundColorElementActive : "");
const LeftContainer = styled.div.withConfig({
  displayName: "DebugGameHistoryLogUI__LeftContainer",
  componentId: "sc-1t8gymb-2"
})(["display:flex;flex-grow:1;flex-shrink:0;"]);
const RightContainer = styled.div.withConfig({
  displayName: "DebugGameHistoryLogUI__RightContainer",
  componentId: "sc-1t8gymb-3"
})(["display:flex;flex-grow:0;flex-shrink:1;"]);
const LogData = styled.div.withConfig({
  displayName: "DebugGameHistoryLogUI__LogData",
  componentId: "sc-1t8gymb-4"
})(["margin-right:8px;", ";"], props => props.highlightAsWarning ? "color: " + FontColorWarn : "");
const LogDataTime = styled(LogData).withConfig({
  displayName: "DebugGameHistoryLogUI__LogDataTime",
  componentId: "sc-1t8gymb-5"
})(["color:", ";"], FontColorLowlight);
const LogDataEntity = styled(LogData).withConfig({
  displayName: "DebugGameHistoryLogUI__LogDataEntity",
  componentId: "sc-1t8gymb-6"
})(["color:", ";"], FontColorHighlight);
const Row = styled.div.withConfig({
  displayName: "DebugGameHistoryLogUI__Row",
  componentId: "sc-1t8gymb-7"
})(["display:flex;flex-direction:row;"]);
const RowContainer = styled.div.withConfig({
  displayName: "DebugGameHistoryLogUI__RowContainer",
  componentId: "sc-1t8gymb-8"
})(["display:flex;flex-direction:column;"]);
const IconButton = styled.div.withConfig({
  displayName: "DebugGameHistoryLogUI__IconButton",
  componentId: "sc-1t8gymb-9"
})(["display:flex;justify-content:center;background-color:", ";&:hover{background-color:", ";}margin-right:8px;cursor:pointer;border-radius:", ";", ""], BackgroundColorElement, BackgroundColorElementActive, UiConstants.BORDER_RADIUS, BorderPrimary);
const Checkbox = styled.label.withConfig({
  displayName: "DebugGameHistoryLogUI__Checkbox",
  componentId: "sc-1t8gymb-10"
})(["display:flex;flex-direction:row;align-items:center;margin-right:8px;"]);
const LogEntryView = observer(({
  entry
}) => {
  const {
    t
  } = useTranslation();
  return /*#__PURE__*/React.createElement(LogEntryContainer, {
    useSeparator: entry.useSeparator
  }, /*#__PURE__*/React.createElement(LogDataTime, null, entry.time), /*#__PURE__*/React.createElement(RowContainer, null, /*#__PURE__*/React.createElement(Row, null, /*#__PURE__*/React.createElement(LogData, {
    highlightAsWarning: entry.isWarning
  }, t(entry.executionType)), entry.executionEntity && /*#__PURE__*/React.createElement(LogDataEntity, null, "'", t(entry.executionEntity), "'"), entry.executionEntityId && /*#__PURE__*/React.createElement(GUITextDebug, null, "id:\xA0", /*#__PURE__*/React.createElement(GUISelectableText, null, toHumanReadableId(entry.executionEntityId)))), /*#__PURE__*/React.createElement(Row, null, entry.executionSourceEntity && /*#__PURE__*/React.createElement(React.Fragment, null, t("game.debug_log_triggered_by"), "\xA0", /*#__PURE__*/React.createElement(LogDataEntity, null, "'", t(entry.executionSourceEntity), "'"), /*#__PURE__*/React.createElement(GUITextDebug, null, "id:\xA0", /*#__PURE__*/React.createElement(GUISelectableText, null, toHumanReadableId(entry.executionSourceId)))))));
});

const Logs = ({
  autoRefresh
}) => {
  const scrollContainer = useRef(null);

  function scrollToBottom() {
    if (scrollContainer.current) {
      scrollContainer.current.scrollTop = scrollContainer.current.scrollHeight;
    }
  }

  useEffect(() => {
    if (!autoRefresh) return;
    scrollToBottom();
  });
  /* Throttle mobx gameStore.gameLog updates to x times per second.
   * 
   * I have yet to find a better method to do this than
   * 1. counting changes with an observable counter (which is only a smidge cleaner manually looping
   *    through the array and touching every logEntry.id just to get an update) and
   * 2. manually forcing a (throttled) update afterwards.
   * 
   * Just assigning gameStore.gameLog via a useState doesn't work because useState is shallow, and I
   * don't want to copy the array via [...gameStore.gameLog] for every single update.
   */

  const forceUpdate = useThrottledForceUpdate(2);
  useEffect(() => {
    if (!autoRefresh) return undefined;
    return reaction(() => gameStore.gameLogChangeCounter, forceUpdate);
  }, [autoRefresh]);
  return /*#__PURE__*/React.createElement(LogContainer, {
    ref: scrollContainer
  }, gameStore.gameLog.map(entry => /*#__PURE__*/React.createElement(LogEntryView, {
    key: entry.id,
    entry: entry
  })));
};

export const GameLogLengthDisplay = observer(() => {
  return /*#__PURE__*/React.createElement("span", null, gameStore.gameLogLength);
});
export const DebugGameHistoryLogUI = observer(() => {
  const {
    t
  } = useTranslation();

  async function copyLog() {
    let completeLog = "";
    gameStore.gameLog.forEach(log => completeLog += log.toString(t) + "\n");
    await navigator.clipboard.writeText(completeLog);
  }

  function toggleLimit() {
    if (gameStore.gameLogLimit == 100) gameStore.setGameLogLimit(-1);else gameStore.setGameLogLimit(100);
  }

  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(SectionHeader, null, /*#__PURE__*/React.createElement(LeftContainer, null, /*#__PURE__*/React.createElement(GUIHeadline, null, t("game.debug_headline_history_log"), " "), /*#__PURE__*/React.createElement(GUIHeadlineSuffix, null, "(", /*#__PURE__*/React.createElement(GameLogLengthDisplay, null), ")")), /*#__PURE__*/React.createElement(RightContainer, null, /*#__PURE__*/React.createElement(Checkbox, null, /*#__PURE__*/React.createElement(InputWithMargin, {
    type: "checkbox",
    checked: gameStore.debugLogAndTriggerAutoRefresh,
    onChange: gameStore.toggleDebugLogAndTriggerAutoRefresh
  }), t("game.debug_auto_refresh")), /*#__PURE__*/React.createElement(Checkbox, null, /*#__PURE__*/React.createElement(InputWithMargin, {
    type: "checkbox",
    checked: gameStore.gameLogLimit == 100,
    onChange: toggleLimit
  }), t("game.debug_max_log_entries", {
    count: 100
  })), /*#__PURE__*/React.createElement(IconButton, {
    onClick: _ => copyLog()
  }, /*#__PURE__*/React.createElement(IoIosCopy, null), t("game.debug_log_button_copy")), /*#__PURE__*/React.createElement(IconButton, {
    onClick: _ => gameStore.clearLog()
  }, /*#__PURE__*/React.createElement(VscClearAll, null), t("game.debug_log_button_clear")))), /*#__PURE__*/React.createElement(Logs, {
    autoRefresh: gameStore.debugLogAndTriggerAutoRefresh
  }));
});